import { useEffect, useState } from 'react';
import { Plus, Building2, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
} from '@/components/common';
import { companiesApi, Company, QueryCompanyParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; company?: Company }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  const fetchCompanies = async (params?: QueryCompanyParams) => {
    try {
      setLoading(true);
      const response = await companiesApi.getAll(params);
      setCompanies(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies({ search: searchQuery || undefined });
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!deleteDialog.company) return;

    try {
      setDeleting(true);
      await companiesApi.delete(deleteDialog.company.id);
      toast.success('Company deleted successfully');
      setDeleteDialog({ open: false });
      fetchCompanies({ search: searchQuery || undefined });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<Company>[] = [
    {
      key: 'name',
      header: 'Company',
      sortable: true,
      render: (company) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{company.displayName}</div>
            <div className="text-sm text-muted-foreground">{company.slug}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (company) => (
        <StatusBadge
          status={company.type === 'PRIMARY_ENTERPRISE' ? 'active' : 'pending'}
          label={company.type === 'PRIMARY_ENTERPRISE' ? 'Enterprise' : 'Partner'}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (company) => (
        <StatusBadge status={company.status.toLowerCase() as any} />
      ),
    },
    {
      key: '_count',
      header: 'Users',
      render: (company) => company._count?.users || 0,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (company) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, company })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader
        title="Companies"
        description="Manage companies and partner organizations"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Companies' },
        ]}
        actions={
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Company
          </Button>
        }
      />

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search companies..."
          className="max-w-sm"
        />
      </div>

      <DataTable
        data={companies}
        columns={columns}
        loading={loading}
        emptyTitle="No companies found"
        emptyDescription="Get started by creating your first company."
        emptyAction={
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Company
          </Button>
        }
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Delete Company"
        description={`Are you sure you want to delete "${deleteDialog.company?.displayName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </div>
  );
}
